import type * as t from "./types";

import Quiz from "./Quiz.svelte";
import { Telemetry } from "./telemetry";
import { camelCaseToCssVariable, getHostData, hexToRgb } from "./utils";

export class QuizElement extends HTMLElement {
    private props: t.ShopifyProps;
    private quiz?: Quiz;
    private telemetry = new Telemetry();

    constructor() {
        super();

        if (!this.shadowRoot) {
            this.attachShadow({
                mode: "open",
            });
        }

        const instanceId = this.dataset.instanceId;
        if (instanceId == null) {
            throw new Error(
                "[shpln-quiz] Instance id not found in <shpln-quiz> dataset.",
            );
        }

        const { props, cssSources } = getHostData(instanceId);

        this.props = props as t.ShopifyProps;
        this.setupCSS(cssSources, this.props);

        // Commented this out because we currently don't use a theme anymore,
        // but is very likely that we will in the future.
        this.setupTheme(props.theme);
    }

    connectedCallback() {
        if (this.quiz != null) {
            return;
        }
        this.init();
    }

    private onCssLoaded() {
        this.isCssLoaded = true;
        // this.quiz?.setReady(true);
        this.dispatchEvent(new CustomEvent("css-loaded"));
    }

    private setupCSS(sources: string[], props: t.ShopifyProps) {
        const sectionContainer = document.querySelector(
            `#${props.shopifySectionId}`,
        );
        const sectionCustomStyle =
            sectionContainer?.querySelector("style:last-of-type");

        if (sectionCustomStyle != null) {
            const sectionCustomCss = sectionCustomStyle.innerHTML;
            const shadowCustomStyle = document.createElement("style");
            const shadowCustomCss = sectionCustomCss.replace(
                `#${props.shopifySectionId}`,
                "",
            );
            shadowCustomStyle.innerHTML = shadowCustomCss;
            this.shadowRoot?.prepend(shadowCustomStyle);
        }

        const linkTags: HTMLLinkElement[] = [];
        // Loop backwards because we are prepending each item. This way we can
        // preserve the order of CSS imports.
        for (let i = sources.length - 1; i >= 0; i--) {
            const source = sources[i];
            const link = document.createElement("link");
            link.rel = "stylesheet";
            link.href = source;
            this.shadowRoot?.prepend(link);
            linkTags.push(link);
        }

        // Listen to all link tags 'load' events. When all are loaded, a callback
        // is called.
        let semaphore = linkTags.length;
        for (const linkTag of linkTags) {
            linkTag.addEventListener("load", () => {
                --semaphore;
                if (semaphore === 0) {
                    this.onCssLoaded();
                }
            });
        }
    }

    private setupTheme(theme: t.Theme) {
        const values = [];
        for (const [key, value] of Object.entries(theme)) {
            const varName = camelCaseToCssVariable(key);
            switch (true) {
                case value.startsWith("#"):
                    const { r, g, b } = hexToRgb(value);
                    values.push(`${varName}-rgb: ${r} ${g} ${b};`);
                default:
                    values.push(`${varName}: ${value};`);
            }
        }

        const style = document.createElement("style");
        style.textContent = `:host{${values.join(" ")}}`;
        this.shadowRoot?.append(style);
    }

    private init() {
        if (this.shadowRoot == null) {
            console.error(
                "[shpln-quiz] Cannot initialize quiz because shadow root was not found",
            );
            return;
        }

        if (this.props == null) {
            console.error(
                "[shpln-quiz] Cannot initialize quiz because props were not found",
            );
            return;
        }

        if (this.props.presentationStyle === "drawer") {
            this.setupDrawer();
        }

        this.quiz = new Quiz({
            target: this.shadowRoot,
            props: {
                hostProps: this.props,
                host: this,
            },
        });
    }

    private get wrapper() {
        const wrapper =
            document.querySelector(
                `#${this.props.shopifySectionId} .shpln-quiz-wrapper`,
            ) ?? document.querySelector(`.shpln-quiz-wrapper`);

        if (wrapper == null) {
            console.error("[shpln-quiz] Error: .shpln-quiz-wrapper not found");
            return;
        }
        return wrapper;
    }

    public start() {
        this.presentAsDrawer();
        this.wrapper?.setAttribute("quiz-status", "started");
        this.quiz?.start();
    }

    public onStart(cb: () => void) {
        if (this.quiz == null) {
            console.error(
                "[shpln-quiz] Trying to set onStart callback before quiz is instantiated",
            );
            return;
        }
        this.quiz.onStart(cb);
    }

    public presentAsDrawer() {
        window.requestAnimationFrame(() => {
            this.wrapper?.setAttribute("visible", "totally");
        });

        const drawer = this.wrapper?.querySelector(".shpln-quiz__drawer");
        this.wrapper?.setAttribute("visible", "totally");
        drawer?.setAttribute("open", "you_bet");
        drawer?.removeAttribute("closed");

        this.telemetry.logEvent("drawer_open");
    }

    private closeDrawer() {
        window.requestAnimationFrame(() => {
            this.wrapper?.removeAttribute("visible");
            const drawer = this.wrapper?.querySelector(".shpln-quiz__drawer");
            drawer?.setAttribute("closed", "yepsies");
            drawer?.removeAttribute("open");
        });
        this.telemetry.logEvent("drawer_close");
    }

    private setupDrawer() {
        const closeButton = this.wrapper?.querySelector(
            ".shpln-quiz-close-button",
        );
        closeButton?.addEventListener("click", () => {
            this.closeDrawer();
        });

        const delay = this.props.drawerConfig.delayInSeconds * 1000;
        // If the delay is zero, this means that the drawer should be invoked
        // directly rather than appearig after some time.
        if (delay === 0) {
            return;
        }

        setTimeout(() => {
            this.presentAsDrawer();
        }, delay);
    }
}

if (customElements.get("shpln-quiz") == null) {
    customElements.define("shpln-quiz", QuizElement);
}
